import { SidebarTypes } from "enums/SidebarTypes.enum";
import { AppRoutes } from "enums/routes.enum";
import { isValidUrl } from "helpers/isValidUrl";
import { useCurrentPath } from "hooks";
import { useDeviceDetect } from "hooks/useDeviceDetect";
import { useAppData } from "providers/RootStoreProvider";
import { RefObject, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export const useHeaderLogic = ({
    sidebarRef,
    sidebarTogglerRef,
    languagePickerRef,
}: {
    sidebarRef: RefObject<HTMLDivElement | null>;
    sidebarTogglerRef: RefObject<HTMLButtonElement | null>;
    languagePickerRef: RefObject<HTMLDivElement | null>;
}) => {
    const navigate = useNavigate();
    const { isTablet } = useDeviceDetect();
    const { flowOuter } = useAppData();
    const [searchParams] = useSearchParams();
    const { isHomePage, isAccountPage, isSearchPage, isLegalPage, isPaymentErrorPage, isPaymentPage, isLostPhonePage } = useCurrentPath();

    const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
    const [languagePickerOpen, setLanguagePickerOpen] = useState(false);
    const [sidebar, setSidebar] = useState({
        type: SidebarTypes.Navigation,
        isOpen: false,
    });
    const redirectSearchParam = searchParams.get("redirect");

    const isPAymentFlowOuter = isPaymentPage && !flowOuter;

    const isCloseButtonShown = (!isHomePage && !isLostPhonePage && !isSearchPage && !isAccountPage && !isPaymentPage) || isPAymentFlowOuter;
    const isMenuTogglerShown = isTablet && (isHomePage || isAccountPage || isSearchPage || isLostPhonePage);
    const renderLoginButton = isHomePage || isAccountPage || isSearchPage || isLostPhonePage;
    const renderNavMenu = !isTablet && (isHomePage || isLostPhonePage);
    const isNavigateBack = isLegalPage || isPaymentErrorPage;
    const disabledLogoRedirect = isAccountPage;

    const handleLogoClick = (e) => {
        if (disabledLogoRedirect) e.preventDefault();

        const isDoubleScreenScrolled = window.scrollY >= window.innerHeight * 2;

        if (isDoubleScreenScrolled) {
            e.preventDefault();
            window.scrollTo(0, 0);
        }
    };

    const toggleSidebar = (type?: SidebarTypes) => {
        if (type) {
            setSidebar({ isOpen: !sidebar.isOpen, type });
        } else {
            setSidebar({ ...sidebar, isOpen: !sidebar.isOpen });
        }
    };

    const handleClickOutsideLanguagePicker = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        if (languagePickerRef.current && !languagePickerRef.current.contains(target) && !isTablet) {
            setLanguagePickerOpen(false);
        }
    };

    const handleClickOutsideSidebar = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        if (
            sidebarRef.current &&
            !sidebarRef.current.contains(target) &&
            !sidebarTogglerRef.current?.contains(target) &&
            !languagePickerRef.current?.contains(target)
        ) {
            setSidebar({ ...sidebar, isOpen: false });
        }
    };

    useEffect(() => {
        if (languagePickerOpen) {
            document.addEventListener("click", handleClickOutsideLanguagePicker);

            return () => {
                document.removeEventListener("click", handleClickOutsideLanguagePicker);
            };
        }
    }, [languagePickerOpen]);

    useEffect(() => {
        if (sidebar.isOpen) {
            document.addEventListener("click", handleClickOutsideSidebar);

            return () => {
                document.removeEventListener("click", handleClickOutsideSidebar);
            };
        }
    }, [sidebar.isOpen]);

    const handleLanguagePickerClick = () => {
        if (isTablet) {
            if (sidebar.isOpen && sidebar.type === SidebarTypes.LanguageMenu) {
                setSidebar({ ...sidebar, isOpen: false });
            } else if (sidebar.isOpen && sidebar.type !== SidebarTypes.LanguageMenu) {
                setSidebar({ ...sidebar, type: SidebarTypes.LanguageMenu });
            } else {
                setSidebar({
                    isOpen: true,
                    type: SidebarTypes.LanguageMenu,
                });
            }
        } else {
            setLanguagePickerOpen(!languagePickerOpen);
        }
    };

    const handleCloseButtonClick = () => {
        if (isValidUrl(redirectSearchParam)) {
            if (redirectSearchParam) window.location.href = redirectSearchParam;
        } else {
            if (isPaymentPage) {
                navigate(AppRoutes.ACCOUNT);
            } else if (isNavigateBack && window.history.length > 1) {
                navigate(-1);
            } else {
                navigate(AppRoutes.HOME);
            }
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            if (scrollY > 50) {
                setIsHeaderScrolled(true);
            } else {
                setIsHeaderScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return {
        sidebar,
        isCloseButtonShown,
        isMenuTogglerShown,
        renderLoginButton,
        isHeaderScrolled,
        isHomePage,
        renderNavMenu,
        isAccountPage,
        isLegalPage,
        isPaymentPage,
        languagePickerOpen,
        handleCloseButtonClick,
        handleLanguagePickerClick,
        toggleSidebar,
        handleLogoClick,
        setSidebar,
        setLanguagePickerOpen,
    };
};
